import type { ReactNode } from 'react'
import { Button, Drawer } from '@crosscode/gravity-components'
import { NODE_META } from './constants'
import { DeviceActions, NODE_ICONS } from './DeviceActions'
import type { InfraTableRow } from './utils'
import styles from '@/pages/HomePage.module.css'

interface Props {
  open: boolean
  row: InfraTableRow | null
  storeId: string
  utm?: Record<string, unknown>
  chz?: Record<string, unknown>
  onClose: () => void
  onReload: () => void
}

function MetaRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.metaRow}>
      <span className={styles.metaKey}>{label}</span>
      <span className={styles.metaVal}>{children}</span>
    </div>
  )
}

export function DeviceDetailsDrawer({ open, row, storeId, utm, chz, onClose, onReload }: Props) {
  if (!row) {
    return <Drawer title="Устройство" open={open} onClose={onClose} size={520} />
  }

  const online = row.status === 'online'
  const offline = row.status === 'offline'
  const statusText = offline ? 'Оффлайн' : online ? 'Онлайн' : 'Неизвестно'
  const meta = NODE_META[row.nodeType]

  const chzOk = row.nodeType === 'chz' && chz && !chz.error
  const utmOk = row.nodeType === 'egais_utm' && utm && !utm.error

  return (
    <Drawer title={row.title} open={open} onClose={onClose} size={540}>
      <div className={styles.drawerHero}>
        <span className={styles.drawerIcon}>{NODE_ICONS[row.nodeType]}</span>
        <div className={styles.drawerHeroText}>
          <div className={styles.drawerType}>{meta.titleHint}</div>
          <span
            className={[
              styles.statusCell,
              online ? styles.statusOn : '',
              offline ? styles.statusOff : '',
              !online && !offline ? styles.statusMuted : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <i className={styles.statusDot} aria-hidden />
            {statusText}
            {row.ping ? <span className={styles.statusPing}> · {row.ping}</span> : null}
          </span>
        </div>
      </div>

      <div className={styles.drawerMeta}>
        <MetaRow label="IP">
          <span className={styles.mono}>{row.ip || '—'}</span>
        </MetaRow>
        <MetaRow label="Ping">{row.ping || '—'}</MetaRow>
        {chzOk ? (
          <>
            <MetaRow label="Версия">{String(chz.version ?? '—')}</MetaRow>
            <MetaRow label="Наименование">{String(chz.name ?? '—')}</MetaRow>
            <MetaRow label="ИНН">{String(chz.inn ?? '—')}</MetaRow>
            <MetaRow label="Идентификатор">{String(chz.inst ?? '—')}</MetaRow>
            <MetaRow label="Синхронизация">{String(chz.lastSync ?? '—')}</MetaRow>
            <MetaRow label="Обновление">{String(chz.lastUpdate ?? '—')}</MetaRow>
          </>
        ) : null}
        {utmOk ? (
          <>
            <MetaRow label="Версия">{String(utm.version ?? '—')}</MetaRow>
            <MetaRow label="Контур">{String(utm.contour ?? '—')}</MetaRow>
            <MetaRow label="Лицензия">{String(utm.license ?? '—')}</MetaRow>
          </>
        ) : null}
        {utm?.error ? (
          <MetaRow label="ЕГАИС УТМ">
            <span className={styles.errorLine}>{String(utm.error)}</span>
          </MetaRow>
        ) : null}
        {chz?.error ? (
          <MetaRow label="Честный Знак">
            <span className={styles.errorLine}>{String(chz.error)}</span>
          </MetaRow>
        ) : null}
      </div>

      <div className={styles.drawerActions}>
        <DeviceActions
          node={row}
          id={row.nodeId}
          storeId={storeId}
          chz={chz}
          onReload={() => {
            onReload()
            onClose()
          }}
        />
        <Button onClick={onClose}>Закрыть</Button>
      </div>
    </Drawer>
  )
}
